import { Link } from "react-router-dom";
import { assetUrl } from "@/lib/api";
import type { ClubFull, HeroButton } from "@/lib/types";

interface HeroProps {
  club: ClubFull | null;
}

// 관리자에서 아직 값을 못 받았을 때만 쓰는 기본값.
// 로딩 후에는 서버가 준 값을 그대로 그린다(지운 문구가 되살아나지 않게).
const DEFAULT_BG =
  "https://readdy.ai/api/search-image?query=Dreamy%20dandelion%20field%20at%20golden%20hour%20with%20soft%20warm%20sunlight%2C%20floating%20dandelion%20seeds%20drifting%20in%20the%20gentle%20breeze%2C%20artistic%20stylized%20illustration%2C%20soft%20pastel%20yellow%20and%20warm%20cream%20tones%2C%20shallow%20depth%20of%20field%20bokeh%2C%20serene%20hopeful%20community%20atmosphere%2C%20clean%20minimal%20composition%2C%20gentle%20golden%20glow%2C%20high%20detail%2C%20harmonious%20warm%20color%20palette&width=1600&height=900&seq=hero-dandelion-01&orientation=landscape";
const DEFAULT_DESC =
  "대전 지역의 전문가들이 모여 봉사와 나눔, 그리고 회원 간 비즈니스 연결을 실천하는 로타리클럽입니다.";
const DEFAULT_BUTTONS: HeroButton[] = [
  { text: "민들레 비즈맵", url: "/bizmap", style: "primary" },
  { text: "클럽소개", url: "/about", style: "ghost" },
];

const PRIMARY_BTN_CLASS =
  "w-full sm:w-auto whitespace-nowrap rounded-full bg-secondary-500 px-7 py-3.5 text-sm font-semibold text-primary-950 hover:bg-secondary-600 transition-colors";
const GHOST_BTN_CLASS =
  "w-full sm:w-auto whitespace-nowrap rounded-full border border-background-50/40 bg-background-50/10 px-7 py-3.5 text-sm font-semibold text-background-50 hover:bg-background-50/20 transition-colors";

export default function Hero({ club }: HeroProps) {
  const loaded = !!club;
  const hero = club?.hero;

  // 배경: hero.bg 가 있으면 그 이미지, 없으면 지금 쓰는 기본 배경 유지
  const bg = hero?.bg ? assetUrl(hero.bg) : DEFAULT_BG;

  // 어둡게 %: «항상» 서버 값을 쓴다(고정값 금지)
  const dimNum = Number(hero?.dim ?? 25);
  const dimAlpha =
    (Number.isFinite(dimNum) ? Math.min(Math.max(dimNum, 0), 80) : 25) / 100;

  // 각 문구는 로딩 중일 때만 기본값, 로딩 후엔 서버 값(빈 문자열이면 숨김)
  const badge = hero?.badge ?? (loaded ? "" : "대전민들레 로타리클럽");
  const title1 = hero?.title1 ?? (loaded ? "" : "봉사의 씨앗을");
  const title2 = hero?.title2 ?? (loaded ? "" : "멀리 퍼뜨립니다");
  const sub = hero?.sub ?? (loaded ? "" : club?.district || "국제로타리 3680지구");
  const since = hero?.since ?? (loaded ? "" : "");
  const desc = hero?.desc ?? (loaded ? "" : DEFAULT_DESC);
  const showStat = hero?.show_stat != null ? Number(hero.show_stat) === 1 : true;
  const buttons = hero?.buttons ?? (loaded ? [] : DEFAULT_BUTTONS);

  const memberCnt = club?.stats?.member;
  const years = club?.stats?.years ?? club?.years;
  const hasMember = memberCnt != null && String(memberCnt) !== "";
  const hasYears = years != null && String(years) !== "";
  const showSummary = showStat && (hasMember || hasYears);

  return (
    <section className="relative w-full overflow-hidden">
      {/* 배경 이미지 */}
      <div className="absolute inset-0">
        <img
          src={bg}
          alt={title1 ? `${title1} ${title2}`.trim() : "메인 배너 배경"}
          className="h-full w-full object-cover object-top"
        />
        {/* 배경 위 검은 반투명 레이어 — 투명도는 관리자 «배경 어둡게» 값 */}
        <div
          className="absolute inset-0"
          style={{ backgroundColor: `rgba(0, 0, 0, ${dimAlpha})` }}
          aria-hidden="true"
        ></div>
      </div>

      {/* 콘텐츠 */}
      <div className="relative z-10 flex min-h-[420px] md:min-h-[560px] w-full flex-col items-center justify-center px-4 md:px-6 text-center">
        {badge && (
          <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-sm text-background-50 backdrop-blur">
            <i className="ri-seedling-line" aria-hidden="true"></i>
            {badge}
          </span>
        )}

        {(title1 || title2) && (
          <h1 className="mt-6 max-w-3xl font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight">
            {title1}
            {title1 && title2 ? " " : ""}
            {title2 && <span className="text-secondary-300">{title2}</span>}
          </h1>
        )}

        {(sub || since) && (
          <p className="mt-4 text-sm md:text-base font-medium tracking-wide text-background-100/90">
            {sub}
            {sub && since ? " · " : ""}
            {since && <span className="text-secondary-200">{since}</span>}
          </p>
        )}

        {showSummary && (
          <p className="mt-2 inline-flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm md:text-base font-semibold text-background-50">
            {hasMember && <span>{`재적회원 ${memberCnt}명`}</span>}
            {hasMember && hasYears && (
              <span className="text-background-50/40" aria-hidden="true">
                |
              </span>
            )}
            {hasYears && <span>{`창립 ${years}주년`}</span>}
          </p>
        )}

        {desc && (
          <p className="mt-5 max-w-xl text-base md:text-lg text-background-100/90 leading-relaxed">
            {desc}
          </p>
        )}

        {buttons.length > 0 && (
          <div className="mt-8 flex flex-col sm:flex-row items-center gap-3">
            {buttons.map((btn, i) => {
              const cls = btn.style === "primary" ? PRIMARY_BTN_CLASS : GHOST_BTN_CLASS;
              const isExternal = /^https?:\/\//i.test(btn.url);
              if (isExternal) {
                return (
                  <a
                    key={`${btn.text}-${i}`}
                    href={btn.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cls}
                  >
                    {btn.text}
                  </a>
                );
              }
              return (
                <Link key={`${btn.text}-${i}`} to={btn.url || "/"} className={cls}>
                  {btn.text}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}